import React, { useContext, useEffect, useMemo, useState } from 'react'

import { Button, Checkbox, Form, Header, Icon, List, Message, Modal, Progress } from 'semantic-ui-react'

import Data, { IProject, IUploadSession, IUserProfile } from '../../api/Data'
import { GlobalContext } from '../../contexts/GlobalContext'
import { UserContext } from '../../contexts/UserContext'

import {
  captureVideoPoster,
  createDeviceMetadata,
  normaliseVideoContentType,
  uploadFileToSession,
} from './directS3Upload'

const DEFAULT_VIDEO_FILE_TYPES = ['mp4', 'avi', 'mov', 'webm']

const VIDEO_MIME_TYPES: { [key: string]: string } = {
  mp4: 'video/mp4',
  avi: 'video/x-msvideo',
  mov: 'video/quicktime',
  webm: 'video/webm',
}

export interface AdminUploadDraft {
  session: IUploadSession
  files: File[]
  contributorNames: string[]
}

export const getAdminUploadRequirements = (project: IProject, userProfile: IUserProfile) => {
  const projectMaxFileSize = Number(project.uploadMaxFileSizeInMb)
  const entitlementMaxFileSize = Number(userProfile.maxSizeOfClipInMb)
  const maxFileSize =
    projectMaxFileSize > 0 ? Math.min(projectMaxFileSize, entitlementMaxFileSize) : entitlementMaxFileSize
  const acceptedFileTypes = (
    Array.isArray(project.acceptedUploadFileTypes) && project.acceptedUploadFileTypes.length > 0
      ? project.acceptedUploadFileTypes
      : DEFAULT_VIDEO_FILE_TYPES
  )
    .map((fileType) => fileType.toLowerCase())
    .filter((fileType) => VIDEO_MIME_TYPES[fileType])

  return {
    maxFileSize,
    maxFiles: Math.max(1, Number(userProfile.plan.maxBatchFiles) || 1),
    minDuration: 2,
    acceptedFileTypes,
    acceptedFileMimeTypes: acceptedFileTypes.map((fileType) => VIDEO_MIME_TYPES[fileType]),
  }
}

const UploadFromComputerModal = ({
  project,
  initialFiles = [],
  initialContributorNames = [],
  stockVideo,
  onUploaded,
  setShowModal,
}: IProps) => {
  const { isMobile } = useContext(GlobalContext)
  const { userProfile } = useContext(UserContext)
  const requirements = useMemo(() => getAdminUploadRequirements(project, userProfile), [project, userProfile])
  const [files, setFiles] = useState<File[]>(initialFiles)
  const [contributorNames, setContributorNames] = useState<string[]>(initialContributorNames)
  const [assignmentAccepted, setAssignmentAccepted] = useState(false)
  const [onBehalfAccepted, setOnBehalfAccepted] = useState(false)
  const [additionalDeclarationAccepted, setAdditionalDeclarationAccepted] = useState(false)
  const [additionalDeclaration, setAdditionalDeclaration] = useState({
    enabled: !!project.additionalDeclarationEnabled,
    text: project.additionalDeclarationText || '',
  })
  const [progress, setProgress] = useState<{ fileName: string; fileNumber: number; percent: number } | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (project.useTeamLegalSettings === false) return
    Data.getMyTeam().then(({ data, error }) => {
      if (error || !data) {
        setError('The team legal settings could not be loaded. Refresh and try again.')
        return
      }
      setAdditionalDeclaration({
        enabled: !!data.defaultAdditionalDeclarationEnabled,
        text: data.defaultAdditionalDeclarationText || '',
      })
    })
  }, [project.useTeamLegalSettings])

  const selectFiles = (selected: FileList | null) => {
    const nextFiles = selected ? Array.from(selected) : []
    setFiles(nextFiles)
    setContributorNames(nextFiles.map((_, index) => contributorNames[index] || ''))
    setError(null)
  }

  const validate = () => {
    if (!files.length) return 'Choose at least one video.'
    if (files.length > requirements.maxFiles) return `Choose no more than ${requirements.maxFiles} videos.`
    const invalidType = files.find(
      (file) => !requirements.acceptedFileMimeTypes.includes(normaliseVideoContentType(file))
    )
    if (invalidType) return `${invalidType.name} is not an accepted video type.`
    const invalidSize = files.find((file) => file.size > requirements.maxFileSize * 1024 * 1024)
    if (invalidSize) return `${invalidSize.name} exceeds the ${requirements.maxFileSize} MB limit.`
    if (contributorNames.some((name) => !name.trim())) return 'Enter the original content creator for every video.'
    if (!assignmentAccepted || !onBehalfAccepted) return 'Confirm both upload declarations to continue.'
    if (additionalDeclaration.enabled && !additionalDeclarationAccepted)
      return 'Confirm the project declaration to continue.'
    return null
  }

  const upload = async () => {
    const validationError = validate()
    if (validationError) return setError(validationError)
    setLoading(true)
    setError(null)

    let session: IUploadSession | null = null
    try {
      const response = await Data.createAdminUploadSession(project.slug, {
        consent: {
          contentAssignmentAccepted: assignmentAccepted,
          adminUploadOnBehalfAccepted: onBehalfAccepted,
          additionalDeclarationAccepted,
        },
        deviceMetadata: {
          ...createDeviceMetadata(),
          ...(stockVideo
            ? {
                uploadSource: 'pexels',
                stockVideoUrl: stockVideo.videoUrl,
                stockContributorUrl: stockVideo.contributorUrl,
              }
            : {}),
        },
        files: files.map((file) => ({
          fileName: file.name,
          contentType: normaliseVideoContentType(file),
          sizeBytes: file.size,
          deviceMetadata: {
            lastModified: file.lastModified || null,
            ...(stockVideo ? { source: 'pexels', stockVideoId: stockVideo.videoId } : {}),
          },
        })),
      })
      if (response.error || !response.data) throw response.error || new Error('Could not start secure upload')
      session = response.data

      for (let index = 0; index < files.length; index++) {
        try {
          const poster = await captureVideoPoster(files[index])
          if (poster) {
            const posterResponse = await Data.uploadUploadSessionPoster(
              session.id,
              session.files[index].id,
              session.token,
              poster
            )
            if (posterResponse.error) throw posterResponse.error
          }
        } catch (posterError) {
          console.warn('Processing poster upload failed', posterError)
        }
        await uploadFileToSession({
          session,
          sourceAsset: session.files[index],
          file: files[index],
          onProgress: (loaded, total) =>
            setProgress({
              fileName: files[index].name,
              fileNumber: index + 1,
              percent: total ? Math.round((loaded / total) * 100) : 0,
            }),
        })
      }

      const trimmedContributorNames = contributorNames.map((name) => name.trim())
      onUploaded({ session, files, contributorNames: trimmedContributorNames })
      setShowModal(false)
    } catch (uploadError: any) {
      if (session) await Data.abortUploadSession(session.id, session.token)
      setError(uploadError?.response?.data?.message || uploadError?.message || 'Some files failed to upload.')
      setLoading(false)
    }
  }

  return (
    <Modal dimmer="blurring" open>
      <Modal.Header>Upload videos as project administrator</Modal.Header>
      <Modal.Content>
        <Header as="h3">
          {stockVideo ? 'Review stock video attribution' : 'Choose videos and add attribution for each one'}
        </Header>
        <p>
          Signed in as <strong>{userProfile.username}</strong> ({userProfile.email}). Your administrator identity will
          be recorded with this upload.
        </p>
        <Form error={!!error}>
          {!stockVideo && (
            <Form.Field>
              <label htmlFor="admin-video-files">Video files</label>
              <input
                id="admin-video-files"
                type="file"
                multiple={requirements.maxFiles > 1}
                accept={requirements.acceptedFileMimeTypes.join(',')}
                disabled={loading}
                onChange={(event) => selectFiles(event.target.files)}
              />
              <small>
                Up to {requirements.maxFiles} files, {requirements.maxFileSize} MB each. Accepted:{' '}
                {requirements.acceptedFileTypes.join(', ').toUpperCase()}.
              </small>
            </Form.Field>
          )}

          {files.length > 0 && (
            <List divided relaxed>
              {files.map((file, index) => (
                <List.Item key={`${file.name}-${file.lastModified}`}>
                  <Icon name="video" />
                  <List.Content style={{ width: 'calc(100% - 2rem)' }}>
                    <List.Header>{file.name}</List.Header>
                    {stockVideo ? (
                      <div style={{ marginTop: '0.5rem' }}>
                        <Header as="h4" style={{ marginBottom: '0.25rem' }}>
                          Attribution
                        </Header>
                        <p>
                          <strong>{contributorNames[index]}</strong>
                          {stockVideo.contributorUrl && (
                            <>
                              {' '}
                              (<a href={stockVideo.contributorUrl}>Pexels profile</a>)
                            </>
                          )}
                        </p>
                      </div>
                    ) : (
                      <Form.Input
                        label="Attribution"
                        placeholder="Person, company or organisation name"
                        value={contributorNames[index] || ''}
                        disabled={loading}
                        onChange={(_, data) => {
                          const next = [...contributorNames]
                          next[index] = String(data.value)
                          setContributorNames(next)
                        }}
                      />
                    )}
                    {!stockVideo && (
                      <small>Enter the person, company or organisation that should be credited for this video.</small>
                    )}
                  </List.Content>
                </List.Item>
              ))}
            </List>
          )}

          <Form.Field>
            <Checkbox
              checked={assignmentAccepted}
              disabled={loading}
              onChange={(_, data) => setAssignmentAccepted(!!data.checked)}
              label="I confirm that the content may be uploaded to this project and used under the project's content assignment terms."
            />
          </Form.Field>
          <Form.Field>
            <Checkbox
              checked={onBehalfAccepted}
              disabled={loading}
              onChange={(_, data) => setOnBehalfAccepted(!!data.checked)}
              label="I confirm that I have permission from the original content creator to upload this content on their behalf."
            />
          </Form.Field>
          {additionalDeclaration.enabled && (
            <Form.Field>
              <Checkbox
                checked={additionalDeclarationAccepted}
                disabled={loading}
                onChange={(_, data) => setAdditionalDeclarationAccepted(!!data.checked)}
                label={additionalDeclaration.text || 'I confirm the additional declaration required by this project.'}
              />
            </Form.Field>
          )}
          {error && <Message error content={error} />}
          {progress && (
            <Progress percent={progress.percent} progress indicating={progress.percent < 100}>
              Uploading file {progress.fileNumber} of {files.length}: {progress.fileName}
            </Progress>
          )}
        </Form>
      </Modal.Content>
      <Modal.Actions>
        <Button fluid={isMobile} disabled={loading} onClick={() => setShowModal(false)}>
          Cancel
        </Button>
        <Button fluid={isMobile} primary loading={loading} onClick={upload}>
          {stockVideo ? 'Add stock video to project' : 'Continue securely'}
        </Button>
      </Modal.Actions>
    </Modal>
  )
}

interface IProps {
  project: IProject
  initialFiles?: File[]
  initialContributorNames?: string[]
  stockVideo?: {
    videoId: number
    videoUrl: string
    contributorUrl: string
  }
  onUploaded: (draft: AdminUploadDraft) => void
  setShowModal: (arg: boolean) => void
}

export default UploadFromComputerModal
